import React, { useState, useEffect, useCallback } from "react";
import {
  MessageSquare,
  Plus,
  Pencil,
  X,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Trash2,
  Send,
} from "lucide-react";
import {
  getSubmissions,
  getForms,
  createForm,
  createVersion,
  updateVersionQuestions,
  publishVersion,
} from "../../dashboard/services/feedbackService";

const PAGE_SIZE = 25;

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

const STATUS_STYLES = {
  published: "bg-emerald-100 text-emerald-700",
  draft: "bg-yellow-100 text-yellow-700",
  archived: "bg-gray-100 text-gray-500",
};

const DEFAULT_NEW_QUESTIONS = () => [
  { _key: "nq1", question_text: "", question_type: "textarea", is_required: false },
];

// ── Response Row ──────────────────────────────────────────────────────────────
const ResponseRow = ({ response, serial }) => {
  const [expanded, setExpanded] = useState(false);

  // spec: submissions[].user.name, submissions[].version.version_number
  const name   = response.user?.name   ?? response.user_name   ?? response.userName   ?? "Unknown";
  const email  = response.user?.email  ?? response.user_email  ?? response.userEmail  ?? "—";
  const verNum = response.version?.version_number ?? response.version_number ?? response.versionNumber ?? "—";
  const submAt = response.submitted_at ?? response.submittedAt ?? response.created_at ?? null;
  const answers = Array.isArray(response.answers) ? response.answers : [];

  return (
    <>
      <tr className="border-b border-surface-border hover:bg-[#FAFAFA] transition-colors">
        <td className="px-4 py-3 text-sm text-brand-muted">{serial}</td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-[#ECE9FF] flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-[#5B4FCF]">{getInitials(name)}</span>
            </div>
            <span className="text-sm font-medium text-brand-text">{name}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-brand-muted">{email}</td>
        <td className="px-4 py-3 text-sm text-brand-muted">v{verNum}</td>
        <td className="px-4 py-3 text-sm text-brand-muted">{answers.length}</td>
        <td className="px-4 py-3 text-sm text-brand-muted">{formatDateTime(submAt)}</td>
        <td className="px-4 py-3">
          <button
            onClick={() => setExpanded((p) => !p)}
            className="p-1.5 rounded-md text-brand-muted hover:bg-[#ECE9FF] hover:text-[#5B4FCF] transition-colors"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-surface-border bg-[#F7F6FF]">
          <td colSpan={7} className="px-8 py-4">
            {answers.length === 0 ? (
              <p className="text-xs text-brand-muted italic">No answers recorded.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {answers.map((a, i) => {
                  // spec: answer has question_snapshot_json.question_text
                  const qText = a.question_snapshot_json?.question_text ?? a.question_text ?? a.questionText ?? `Question ${i + 1}`;
                  const ansVal = a.answer_text != null ? a.answer_text
                    : a.answer_json != null ? (Array.isArray(a.answer_json) ? a.answer_json.join(", ") : String(a.answer_json))
                    : a.answer ?? "—";
                  return (
                    <div key={i} className="flex gap-3">
                      <span className="text-xs font-semibold text-[#5B4FCF] w-5 shrink-0 mt-0.5">{i + 1}.</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-brand-text mb-0.5">{qText}</p>
                        <p className="text-xs text-brand-muted leading-relaxed whitespace-pre-wrap">{ansVal}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

// ── Question Editor Modal ─────────────────────────────────────────────────────
const QuestionEditorModal = ({ versionId, initialQuestions, onClose, onSaved }) => {
  const [questions, setQuestions] = useState(() =>
    (initialQuestions || []).map((q) => ({
      _key: String(q.id ?? q.question_id ?? Math.random()),
      question_text: q.question_text ?? q.questionText ?? "",
      question_type: q.question_type ?? q.questionType ?? "textarea",
      is_required: q.is_required ?? false,
    }))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const addQuestion = () =>
    setQuestions((p) => [
      ...p,
      { _key: String(Date.now()), question_text: "", question_type: "textarea", is_required: false },
    ]);

  const removeQuestion = (key) =>
    setQuestions((p) => p.filter((q) => q._key !== key));

  const updateField = (key, field, value) =>
    setQuestions((p) =>
      p.map((q) => (q._key === key ? { ...q, [field]: value } : q))
    );

  const handleSave = async () => {
    const valid = questions.filter((q) => q.question_text.trim());
    if (valid.length === 0) { setError("Add at least one question."); return; }
    try {
      setSaving(true);
      setError("");
      const payload = valid.map((q, i) => ({
        order_no: i + 1,
        question_text: q.question_text.trim(),
        question_type: q.question_type,
        is_required: q.is_required,
      }));
      await updateVersionQuestions(versionId, payload);
      onSaved();
    } catch (err) {
      setError(err.message || "Failed to save questions.");
    } finally {
      setSaving(false);
    }
  };

  const TYPE_LABELS = {
    textarea: "Text (long)",
    text: "Text (short)",
    rating: "Rating (1–5)",
    single_select: "Single select",
    multi_select: "Multi-select",
    boolean: "Yes / No",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border">
          <h3 className="text-base font-bold text-brand-text">Edit Questions</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-brand-muted hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Warning */}
        <div className="px-6 py-2 bg-yellow-50 border-b border-yellow-100">
          <p className="text-xs text-yellow-700">Saving replaces the full question list for this version.</p>
        </div>

        {/* Questions */}
        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-3">
          {questions.map((q, i) => (
            <div key={q._key} className="flex gap-3 items-start bg-[#FAFAFA] rounded-xl border border-surface-border p-3">
              <span className="text-xs font-bold text-[#5B4FCF]/40 mt-2 w-4 shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0 flex flex-col gap-2">
                <input
                  type="text"
                  value={q.question_text}
                  onChange={(e) => updateField(q._key, "question_text", e.target.value)}
                  placeholder="Question text..."
                  className="w-full px-3 py-1.5 rounded-lg border border-surface-border text-sm text-brand-text bg-white focus:outline-none focus:ring-2 focus:ring-[#5B4FCF]/25 focus:border-[#5B4FCF] transition"
                />
                <div className="flex items-center gap-3">
                  <select
                    value={q.question_type}
                    onChange={(e) => updateField(q._key, "question_type", e.target.value)}
                    className="px-2.5 py-1.5 rounded-lg border border-surface-border text-xs text-brand-text bg-white focus:outline-none focus:ring-2 focus:ring-[#5B4FCF]/25 transition"
                  >
                    {Object.entries(TYPE_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                  {/* <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={q.is_required}
                      onChange={(e) => updateField(q._key, "is_required", e.target.checked)}
                      className="accent-[#5B4FCF]"
                    />
                    <span className="text-xs text-brand-muted">Required</span>
                  </label> */}
                </div>
              </div>
              <button
                onClick={() => removeQuestion(q._key)}
                className="mt-1.5 p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}

          <button
            onClick={addQuestion}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-surface-border text-sm text-brand-muted hover:border-[#5B4FCF] hover:text-[#5B4FCF] transition-colors"
          >
            <Plus className="w-4 h-4" /> Add question
          </button>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-surface-border flex items-center justify-between">
          {error ? (
            <p className="text-xs text-red-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{error}</p>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-brand-muted hover:bg-gray-100 transition-colors">Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#5B4FCF] text-white hover:bg-[#4a40b8] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? "Saving…" : "Save Questions"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Create Form Modal ─────────────────────────────────────────────────────────
const CreateFormModal = ({ onClose, onCreated }) => {
  const [title, setTitle]         = useState("");
  const [description, setDesc]    = useState("");
  const [questions, setQuestions] = useState(DEFAULT_NEW_QUESTIONS);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  const addQuestion = () =>
    setQuestions((p) => [
      ...p,
      { _key: String(Date.now()), question_text: "", question_type: "textarea", is_required: false },
    ]);

  const removeQuestion = (key) =>
    setQuestions((p) => p.filter((q) => q._key !== key));

  const updateField = (key, field, value) =>
    setQuestions((p) =>
      p.map((q) => (q._key === key ? { ...q, [field]: value } : q))
    );

  const handleCreate = async () => {
    if (!title.trim()) { setError("Title is required."); return; }
    const validQs = questions.filter((q) => q.question_text.trim());
    if (validQs.length === 0) { setError("Add at least one question."); return; }
    const autoKey = title.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    try {
      setSaving(true);
      setError("");
      const res = await createForm({
        key: autoKey,
        title: title.trim(),
        description: description.trim(),
        allow_multiple_submissions: false,
        questions: validQs.map((q, i) => ({
          order_no: i + 1,
          question_text: q.question_text.trim(),
          question_type: q.question_type,
          is_required: q.is_required,
        })),
      });
      const p = res?.data ?? res;
      // spec: createForm response has { data: { form, draftVersion } }
      const vId  = p?.draftVersion?.id ?? p?.version?.id ?? p?.version_id ?? null;
      const vNum = p?.draftVersion?.version_number ?? p?.version?.version_number ?? p?.version_number ?? "1";
      onCreated(vId, vNum);
    } catch (err) {
      const msg = err.message || "";
      setError(
        msg.toLowerCase().includes("already exists")
          ? "A form with this title already exists. Close this and use '+ New Version' on the existing form instead."
          : msg || "Failed to create form."
      );
    } finally {
      setSaving(false);
    }
  };

  const TYPE_LABELS = {
    textarea: "Text (long)",
    text: "Text (short)",
    rating: "Rating (1–5)",
    single_select: "Single select",
    multi_select: "Multi-select",
    boolean: "Yes / No",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border">
          <h3 className="text-base font-bold text-brand-text">Create Feedback Form</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-brand-muted hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
          <div>
            <label className="block text-xs font-semibold text-brand-muted mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Form title..."
              className="w-full px-3.5 py-2.5 rounded-lg border border-surface-border text-sm text-brand-text bg-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#5B4FCF]/25 focus:border-[#5B4FCF] transition"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-brand-muted mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Short description..."
              className="w-full px-3.5 py-2.5 rounded-lg border border-surface-border text-sm text-brand-text bg-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#5B4FCF]/25 focus:border-[#5B4FCF] transition"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-brand-muted mb-2">Questions</label>
            <div className="flex flex-col gap-2">
              {questions.map((q, i) => (
                <div key={q._key} className="flex gap-3 items-start bg-[#FAFAFA] rounded-xl border border-surface-border p-3">
                  <span className="text-xs font-bold text-[#5B4FCF]/40 mt-2 w-4 shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0 flex flex-col gap-2">
                    <input
                      type="text"
                      value={q.question_text}
                      onChange={(e) => updateField(q._key, "question_text", e.target.value)}
                      placeholder="Question text..."
                      className="w-full px-3 py-1.5 rounded-lg border border-surface-border text-sm text-brand-text bg-white focus:outline-none focus:ring-2 focus:ring-[#5B4FCF]/25 focus:border-[#5B4FCF] transition"
                    />
                    <div className="flex items-center gap-3">
                      {/* <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={q.is_required}
                          onChange={(e) => updateField(q._key, "is_required", e.target.checked)}
                          className="accent-[#5B4FCF]"
                        />
                        <span className="text-xs text-brand-muted">Required</span>
                      </label> */}
                    </div>
                  </div>
                  <button
                    onClick={() => removeQuestion(q._key)}
                    className="mt-1.5 p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={addQuestion}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-surface-border text-sm text-brand-muted hover:border-[#5B4FCF] hover:text-[#5B4FCF] transition-colors"
              >
                <Plus className="w-4 h-4" /> Add question
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-surface-border flex items-center justify-between">
          {error ? (
            <p className="text-xs text-red-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{error}</p>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-brand-muted hover:bg-gray-100 transition-colors">Cancel</button>
            <button
              onClick={handleCreate}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#5B4FCF] text-white hover:bg-[#4a40b8] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? "Creating…" : "Create Form"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const AdminFeedbackPage = () => {
  const [activeTab, setActiveTab] = useState("submissions");

  // Submissions state
  const [responses, setResponses]         = useState([]);
  const [responsesTotal, setResponsesTotal] = useState(0);
  const [responsesPage, setResponsesPage] = useState(1);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [responsesError, setResponsesError]   = useState("");

  // Forms state
  const [forms, setForms]           = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [formsError, setFormsError]     = useState("");

  // Modals
  const [createFormModal, setCreateFormModal]   = useState(false);
  const [questionEditor, setQuestionEditor]     = useState(null); // { versionId, questions }
  const [publishConfirm, setPublishConfirm]     = useState(null); // { versionId, versionNumber }
  const [publishing, setPublishing]             = useState(false);
  const [newVersionLoading, setNewVersionLoading] = useState(null); // formId being processed

  // ── Submissions ──
  const fetchSubmissions = useCallback(async (page) => {
    setResponsesLoading(true);
    setResponsesError("");
    try {
      const res = await getSubmissions({ page, limit: PAGE_SIZE });
      const payload = res?.data ?? res;
      const rows  = Array.isArray(payload?.submissions) ? payload.submissions
                  : Array.isArray(payload?.rows)         ? payload.rows
                  : Array.isArray(payload)               ? payload : [];
      // spec: pagination.total
      const total = res?.pagination?.total ?? payload?.pagination?.total ?? payload?.total ?? payload?.count ?? rows.length;
      setResponses(rows);
      setResponsesTotal(total);
    } catch (err) {
      setResponsesError(err.message || "Failed to load submissions.");
    } finally {
      setResponsesLoading(false);
    }
  }, []);

  // ── Forms ──
  const fetchForms = useCallback(async () => {
    setFormsLoading(true);
    setFormsError("");
    try {
      const res = await getForms();
      const payload = res?.data ?? res;
      const list = Array.isArray(payload?.forms) ? payload.forms
                 : Array.isArray(payload?.data)   ? payload.data
                 : Array.isArray(payload)          ? payload : [];
      // Normalise versions — spec: GET /forms returns activeVersion (singular)
      const normalised = list.map((form) => ({
        ...form,
        versions:
          Array.isArray(form.versions)      ? form.versions :
          Array.isArray(form.form_versions) ? form.form_versions :
          form.activeVersion                ? [form.activeVersion] :
          [],
      }));
      setForms(normalised);
    } catch (err) {
      setFormsError(err.message || "Failed to load forms.");
    } finally {
      setFormsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "submissions") fetchSubmissions(responsesPage);
  }, [activeTab, responsesPage, fetchSubmissions]);

  useEffect(() => {
    if (activeTab === "setup") fetchForms();
  }, [activeTab, fetchForms]);

  // ── Publish ──
  const handlePublish = async () => {
    if (!publishConfirm) return;
    setPublishing(true);
    try {
      await publishVersion(publishConfirm.versionId);
      setPublishConfirm(null);
      fetchForms();
    } catch {
      // silently keep modal open so user can retry
    } finally {
      setPublishing(false);
    }
  };

  // ── New Version ──
  const handleNewVersion = async (formId) => {
    setNewVersionLoading(formId);
    try {
      const res = await createVersion(formId, {});
      const payload = res?.data ?? res;
      const vId = payload?.version?.id ?? payload?.version_id ?? payload?.id ?? null;
      const vNum = payload?.version?.version_number ?? payload?.version_number ?? "";
      fetchForms();
      if (vId) setPublishConfirm({ versionId: vId, versionNumber: vNum });
    } finally {
      setNewVersionLoading(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(responsesTotal / PAGE_SIZE));

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#ECE9FF] flex items-center justify-center">
          <MessageSquare className="w-5 h-5 text-[#5B4FCF]" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-brand-text">Feedback</h1>
          <p className="text-sm text-brand-muted">Manage submissions and form setup.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#F4F3FF] rounded-xl p-1 w-fit mb-6">
        {[
          { key: "submissions", label: "Submissions" },
          { key: "setup",       label: "Form Setup"  },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === t.key
                ? "bg-white text-[#5B4FCF] shadow-sm"
                : "text-brand-muted hover:text-brand-text"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── SUBMISSIONS TAB ── */}
      {activeTab === "submissions" && (
        <div className="bg-white rounded-2xl border border-surface-border overflow-hidden">
          {responsesLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-[#5B4FCF]" />
            </div>
          ) : responsesError ? (
            <div className="flex items-center gap-2 text-sm text-red-600 px-6 py-8">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{responsesError}
            </div>
          ) : responses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-brand-muted">
              <MessageSquare className="w-10 h-10 mb-3 opacity-30" />
              <p className="text-sm">No submissions yet.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-surface-border bg-[#FAFAFA]">
                      {["#", "User", "Email", "Version", "Answers", "Submitted", ""].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-brand-muted uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {responses.map((r, i) => (
                      <ResponseRow
                        key={r.id ?? r.submission_id ?? i}
                        response={r}
                        serial={(responsesPage - 1) * PAGE_SIZE + i + 1}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-surface-border">
                  <p className="text-xs text-brand-muted">
                    {responsesTotal} submission{responsesTotal !== 1 ? "s" : ""}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={responsesPage <= 1}
                      onClick={() => setResponsesPage((p) => p - 1)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium border border-surface-border text-brand-muted hover:bg-[#F4F3FF] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <span className="text-xs text-brand-muted">
                      Page {responsesPage} of {totalPages}
                    </span>
                    <button
                      disabled={responsesPage >= totalPages}
                      onClick={() => setResponsesPage((p) => p + 1)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium border border-surface-border text-brand-muted hover:bg-[#F4F3FF] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── FORM SETUP TAB ── */}
      {activeTab === "setup" && (
        <div>
          {/* Header row — always show "New Form" button */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-brand-muted">Configure the feedback form shown to users.</p>
            <button
              onClick={() => setCreateFormModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-[#5B4FCF] text-white hover:bg-[#4a40b8] transition-colors"
            >
              <Plus className="w-4 h-4" /> New Form
            </button>
          </div>

          {formsLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-[#5B4FCF]" />
            </div>
          ) : formsError ? (
            <div className="flex items-center gap-2 text-sm text-red-600 py-8">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{formsError}
            </div>
          ) : forms.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-brand-muted gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#F4F3FF] flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-[#5B4FCF]/40" />
              </div>
              <p className="text-sm">No feedback forms yet. Click <strong>New Form</strong> to create one.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {forms.map((form) => {
                const formId = form.id ?? form.form_id;
                const versions = form.versions ?? [];
                return (
                  <div key={formId} className="bg-white rounded-2xl border border-surface-border overflow-hidden">
                    {/* Form header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border">
                      <div>
                        <p className="text-sm font-bold text-brand-text">{form.title}</p>
                        <p className="text-xs text-brand-muted font-mono mt-0.5">{form.key}</p>
                      </div>
                      <button
                        onClick={() => handleNewVersion(formId)}
                        disabled={newVersionLoading === formId}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#5B4FCF] text-[#5B4FCF] hover:bg-[#ECE9FF] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                      >
                        {newVersionLoading === formId
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Plus className="w-3.5 h-3.5" />}
                        New Version
                      </button>
                    </div>

                    {/* Versions list */}
                    {versions.length === 0 ? (
                      <div className="px-6 py-4 flex items-center gap-2 text-xs text-brand-muted">
                        <span>Click</span>
                        <span className="font-semibold text-[#5B4FCF]">+ New Version</span>
                        <span>to create and publish questions for this form.</span>
                      </div>
                    ) : (
                      <div className="divide-y divide-surface-border">
                        {versions.map((v) => {
                          const vId     = v.id ?? v.version_id;
                          const vNum    = v.version_number ?? v.versionNumber ?? "—";
                          const status  = (v.status || "draft").toLowerCase();
                          const qCount  = Array.isArray(v.questions) ? v.questions.length : (v.question_count ?? v.questionCount ?? null);
                          const created = v.created_at ?? v.createdAt ?? null;
                          return (
                            <div key={vId} className="flex items-center justify-between px-6 py-3">
                              <div className="flex items-center gap-4">
                                <span className="text-sm font-semibold text-brand-text">v{vNum}</span>
                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLES[status] ?? STATUS_STYLES.draft}`}>
                                  {status}
                                </span>
                                {qCount !== null && (
                                  <span className="text-xs text-brand-muted">{qCount} question{qCount !== 1 ? "s" : ""}</span>
                                )}
                                {created && (
                                  <span className="text-xs text-brand-muted">{formatDateTime(created)}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {/* <button
                                  onClick={() => setQuestionEditor({ versionId: vId, questions: Array.isArray(v.questions) ? v.questions : [] })}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-brand-muted border border-surface-border hover:border-[#5B4FCF] hover:text-[#5B4FCF] hover:bg-[#ECE9FF] transition-colors"
                                >
                                  <Pencil className="w-3.5 h-3.5" /> Edit Questions
                                </button> */}
                                {status !== "published" && (
                                  <button
                                    onClick={() => setPublishConfirm({ versionId: vId, versionNumber: vNum })}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                                  >
                                    <Send className="w-3.5 h-3.5" /> Publish
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── MODALS ── */}
      {createFormModal && (
        <CreateFormModal
          onClose={() => setCreateFormModal(false)}
          onCreated={(versionId, versionNumber) => {
            setCreateFormModal(false);
            fetchForms();
            if (versionId) setPublishConfirm({ versionId, versionNumber: versionNumber ?? "1" });
          }}
        />
      )}

      {questionEditor && (
        <QuestionEditorModal
          versionId={questionEditor.versionId}
          initialQuestions={questionEditor.questions}
          onClose={() => setQuestionEditor(null)}
          onSaved={() => { setQuestionEditor(null); fetchForms(); }}
        />
      )}

      {/* Publish confirm overlay */}
      {publishConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-base font-bold text-brand-text mb-2">Publish Version v{publishConfirm.versionNumber}?</h3>
            <p className="text-sm text-brand-muted mb-6">
              Users will immediately see the new questions. This cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setPublishConfirm(null)}
                disabled={publishing}
                className="px-4 py-2 rounded-lg text-sm text-brand-muted hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePublish}
                disabled={publishing}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {publishing && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {publishing ? "Publishing…" : "Publish"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFeedbackPage;
